import { useEffect, useRef } from 'react';
import {
  BrowserRouter,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { BottomNav } from './components/BottomNav';
import { DetailPage } from './pages/DetailPage';
import { EksplorPage } from './pages/EksplorPage';
import { FavoritesPage } from './pages/FavoritesPage';
import { HistoryPage } from './pages/HistoryPage';
import { HomePage } from './pages/HomePage';
import { JadwalPage } from './pages/JadwalPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { PlayerPage } from './pages/PlayerPage';
import { ProfilPage } from './pages/ProfilPage';
import { SearchPage } from './pages/SearchPage';
import { useGoBack } from './lib/hooks';
import { telegram } from './lib/telegram';
import { applyTheme, getTheme } from './lib/theme';

/**
 * Sinkronkan tombol back NATIVE Telegram dengan React Router.
 *
 * PENTING — handler back di-register SEKALI saja (seumur hidup app), lewat
 * ref yang selalu menunjuk `goBack` terbaru. Kalau di-register ulang tiap
 * ganti route, `BackButton.offClick` Telegram yang tidak selalu andal bisa
 * menyisakan handler lama → satu tekan back memicu `navigate(-1)`
 * berkali-kali → lompat ke home melewati halaman sebelumnya. Show/hide
 * tombol diurus di effect terpisah yang memang bergantung pada route.
 */
function TelegramBackSync() {
  const location = useLocation();
  const goBack = useGoBack();
  const goBackRef = useRef(goBack);
  goBackRef.current = goBack;

  // Register handler back TEPAT SATU kali.
  useEffect(() => {
    const handler = (): void => goBackRef.current();
    telegram.onBackButton(handler);
    return () => {
      telegram.offBackButton(handler);
    };
  }, []);

  // Show di semua halaman kecuali home; hide di home.
  useEffect(() => {
    if (location.pathname === '/') {
      telegram.hideBackButton();
    } else {
      telegram.showBackButton();
    }
  }, [location.pathname]);

  return null;
}

/**
 * Kerangka layar bertab: isi halaman + bottom navigation tetap di bawah.
 * Padding bawah menyisakan ruang untuk nav (+ safe area) supaya konten
 * terakhir tidak tertutup.
 */
function TabLayout() {
  return (
    <div className="min-h-screen pb-nav">
      <Outlet />
      <BottomNav />
    </div>
  );
}

export function App() {
  useEffect(() => {
    telegram.ready();
    // Setelah SDK siap, warna header/latar Telegram disamakan lagi dengan
    // tema aktif — sebelum ready() panggilan itu diabaikan Telegram.
    applyTheme(getTheme());
  }, []);

  return (
    <BrowserRouter>
      <TelegramBackSync />
      <Routes>
        {/* Layar bertab — punya bottom navigation. */}
        <Route element={<TabLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/eksplor" element={<EksplorPage />} />
          <Route path="/jadwal" element={<JadwalPage />} />
          <Route path="/favorites" element={<FavoritesPage />} />
          <Route path="/profil" element={<ProfilPage />} />
          <Route path="/history" element={<HistoryPage />} />
        </Route>

        {/* Layar yang di-push — tanpa bottom navigation. */}
        <Route path="/search" element={<SearchPage />} />
        <Route path="/drama/:platform/:id" element={<DetailPage />} />
        <Route
          path="/player/:platform/:id/:chapterId"
          element={<PlayerPage />}
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}
